import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';

import { DetailCard } from '../../Device/details/DetailCard';
import InlineSpinner from '../../shared/InlineSpinner';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM, INPUT_SM, SELECT } from '../../shared/ui';
import { formatTs, formatUserIdentityDisplay } from '../../../helpers/helpers';
import { parseFirmwareListPayload, type FirmwareArtifactRow } from '../../../helpers/firmwareArtifacts';
import type { RaceConfigurationRequirements } from '../../../helpers/types/DeviceValidationTypes';
import { StageReadinessBadge } from './StageReadinessBadge';

type EditableField =
  | 'fw_version'
  | 'fw_version_front'
  | 'min_battery'
  | 'max_debug_size'
  | 'max_race_size';

type RaceConfigurationEditable = {
  fw_version: string;
  fw_file_key: string;
  fw_version_front: string;
  fw_file_key_front: string;
  min_battery: number;
  max_debug_size: number;
  max_race_size: number;
};

type RaceConfigurationMeta = {
  created_at: number;
  created_by: string;
  edited_at: number;
  edited_by: string;
};

/** Suggested defaults when stored values are unset (empty firmware or zero numerics). */
const DEFAULT_FORM: RaceConfigurationEditable = {
  fw_version: '3',
  fw_file_key: '',
  // No fallback default for front firmware (unlike rear's legacy '3'): a guessed
  // version would silently satisfy the "configured" check while systematically
  // failing every front device's real verification. Leave blank until explicitly set.
  fw_version_front: '',
  fw_file_key_front: '',
  min_battery: 3900,
  max_debug_size: 102400,
  max_race_size: 20480,
};

const EDITABLE_FIELDS: EditableField[] = [
  'fw_version',
  'fw_version_front',
  'min_battery',
  'max_debug_size',
  'max_race_size',
];

function toSafeNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeEditable(input: unknown): RaceConfigurationEditable {
  const cfg = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  return {
    fw_version: typeof cfg.fw_version === 'string' ? cfg.fw_version.trim() : '',
    fw_file_key: typeof cfg.fw_file_key === 'string' ? cfg.fw_file_key.trim() : '',
    fw_version_front: typeof cfg.fw_version_front === 'string' ? cfg.fw_version_front.trim() : '',
    fw_file_key_front: typeof cfg.fw_file_key_front === 'string' ? cfg.fw_file_key_front.trim() : '',
    min_battery: toSafeNumber(cfg.min_battery),
    max_debug_size: toSafeNumber(cfg.max_debug_size),
    max_race_size: toSafeNumber(cfg.max_race_size),
  };
}

function mergeWithDefaults(editable: RaceConfigurationEditable): RaceConfigurationEditable {
  return {
    fw_version: editable.fw_version.trim() || DEFAULT_FORM.fw_version,
    fw_file_key: editable.fw_file_key.trim(),
    fw_version_front: editable.fw_version_front.trim(),
    fw_file_key_front: editable.fw_file_key_front.trim(),
    min_battery: editable.min_battery > 0 ? editable.min_battery : DEFAULT_FORM.min_battery,
    max_debug_size: editable.max_debug_size > 0 ? editable.max_debug_size : DEFAULT_FORM.max_debug_size,
    max_race_size: editable.max_race_size > 0 ? editable.max_race_size : DEFAULT_FORM.max_race_size,
  };
}

function normalizeMeta(input: unknown): RaceConfigurationMeta {
  const cfg = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  return {
    created_at: toSafeNumber(cfg.created_at),
    created_by: typeof cfg.created_by === 'string' ? cfg.created_by.trim() : '',
    edited_at: toSafeNumber(cfg.edited_at),
    edited_by: typeof cfg.edited_by === 'string' ? cfg.edited_by.trim() : '',
  };
}

function dispatchToast(kind: 'toast-error' | 'toast-info', message: string): void {
  window.dispatchEvent(new CustomEvent(kind, { detail: message }));
}

function FieldCard({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

type Props = {
  eventId: string;
  raceId: string;
  canEdit: boolean;
  raceCfg: RaceConfigurationRequirements | null;
  raceCfgLoaded: boolean;
  /** Draw attention to the Edit button (e.g. DRAFT race with no configuration yet). */
  pulseEdit?: boolean;
  /** Show the ready/not-ready badge (only meaningful pre-PACKAGING). */
  showReadyBadge?: boolean;
  raceCfgReady?: boolean;
  globalUserIdToDisplayName: Record<string, string>;
  onSaved?: () => void | Promise<void>;
};

export function RaceConfigurationCard(props: Props): JSX.Element {
  const {
    eventId,
    raceId,
    canEdit,
    raceCfg,
    raceCfgLoaded,
    pulseEdit,
    showReadyBadge,
    raceCfgReady,
    globalUserIdToDisplayName,
    onSaved,
  } = props;

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<RaceConfigurationEditable>(DEFAULT_FORM);
  const [originalForm, setOriginalForm] = useState<RaceConfigurationEditable>(DEFAULT_FORM);
  const [meta, setMeta] = useState<RaceConfigurationMeta>({
    created_at: 0,
    created_by: '',
    edited_at: 0,
    edited_by: '',
  });
  const [firmwareArtifacts, setFirmwareArtifacts] = useState<FirmwareArtifactRow[]>([]);
  const [frontFirmwareArtifacts, setFrontFirmwareArtifacts] = useState<FirmwareArtifactRow[]>([]);
  const [firmwareLoading, setFirmwareLoading] = useState(false);

  useEffect(() => {
    // Switching races (or closing the workspace) discards any in-progress edit.
    setEditing(false);
    setLoadError('');
  }, [eventId, raceId]);

  const editedFields = useMemo(() => {
    const fields: Array<{ field: EditableField; before: string | number; after: string | number }> = [];
    for (const field of EDITABLE_FIELDS) {
      if (originalForm[field] !== form[field]) {
        fields.push({ field, before: originalForm[field], after: form[field] });
      }
    }
    return fields;
  }, [form, originalForm]);

  const startEdit = useCallback(async () => {
    setEditing(true);
    setLoadError('');
    setLoading(true);
    setFirmwareLoading(true);
    try {
      const [cfgRes, fwRes, fwFrontRes] = await Promise.all([
        window.eventsAPI?.getRaceConfiguration({ eventId, raceId }),
        window.eventsAPI?.getFirmwareVersions?.('rear'),
        window.eventsAPI?.getFirmwareVersions?.('front'),
      ]);
      const { artifacts } = parseFirmwareListPayload(fwRes, { defaultRole: 'rear' });
      const { artifacts: frontArtifacts } = parseFirmwareListPayload(fwFrontRes, { defaultRole: 'front' });
      setFirmwareArtifacts(artifacts);
      setFrontFirmwareArtifacts(frontArtifacts);

      if (!cfgRes?.ok) {
        setLoadError(String(cfgRes?.error ?? 'Failed to load race configuration'));
        return;
      }
      const cfg = (cfgRes.data as { configuration?: Record<string, unknown> } | undefined)?.configuration ?? {};
      let normalized = normalizeEditable(cfg);
      normalized = mergeWithDefaults(normalized);
      if (!normalized.fw_file_key && normalized.fw_version) {
        const match = artifacts.find((a) => a.version === normalized.fw_version);
        if (match) normalized = { ...normalized, fw_file_key: match.key };
      }
      if (!normalized.fw_file_key_front && normalized.fw_version_front) {
        const match = frontArtifacts.find((a) => a.version === normalized.fw_version_front);
        if (match) normalized = { ...normalized, fw_file_key_front: match.key };
      }
      setForm(normalized);
      setOriginalForm(normalized);
      setMeta(normalizeMeta(cfg));
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load race configuration');
    } finally {
      setLoading(false);
      setFirmwareLoading(false);
    }
  }, [eventId, raceId]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setLoadError('');
  }, []);

  const handleSave = useCallback(async () => {
    if (!window.eventsAPI?.submitRaceConfiguration) return;
    setSaving(true);
    try {
      const res = await window.eventsAPI.submitRaceConfiguration(eventId, {
        raceId,
        configuration: form,
      });
      if (!res?.ok) {
        dispatchToast('toast-error', res?.error || 'Failed to save race configuration');
        return;
      }
      dispatchToast('toast-info', 'Race configuration saved');
      setEditing(false);
      await Promise.resolve(onSaved?.());
    } catch (err: any) {
      dispatchToast('toast-error', err?.message || 'Failed to save race configuration');
    } finally {
      setSaving(false);
    }
  }, [eventId, raceId, form, onSaved]);

  const selectedFirmwareValue =
    form.fw_file_key && firmwareArtifacts.some((a) => a.key === form.fw_file_key)
      ? form.fw_file_key
      : form.fw_version && !firmwareArtifacts.some((a) => a.key === form.fw_file_key)
        ? '__orphan__'
        : '';

  const selectedFrontFirmwareValue =
    form.fw_file_key_front && frontFirmwareArtifacts.some((a) => a.key === form.fw_file_key_front)
      ? form.fw_file_key_front
      : form.fw_version_front && !frontFirmwareArtifacts.some((a) => a.key === form.fw_file_key_front)
        ? '__orphan__'
        : '';

  const readyBadge = showReadyBadge ? (
    <StageReadinessBadge
      ok={!!raceCfgReady}
      okLabel="Configured"
      missingLabel="Not configured"
    />
  ) : null;

  return (
    <DetailCard
      title="Race configuration"
      right={
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button type="button" onClick={cancelEdit} disabled={saving} className={BTN_SECONDARY_SM}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={loading || saving || editedFields.length === 0}
                title={editedFields.length === 0 ? 'No changes to save' : undefined}
                className={`${BTN_PRIMARY_SM} inline-flex items-center gap-2`}
              >
                {saving ? (
                  <>
                    <InlineSpinner size="xs" variant="current" />
                    Saving…
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </>
          ) : (
            <>
              {readyBadge}
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => void startEdit()}
                  className={[BTN_PRIMARY_SM, pulseEdit ? 'animate-pulse ring-2 ring-blue-300/90 dark:ring-blue-500/80' : ''].join(' ')}
                >
                  Edit
                </button>
              ) : null}
            </>
          )}
        </div>
      }
    >
      {!editing ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <FieldCard label="Firmware">
            <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {raceCfgLoaded ? raceCfg?.fw_version || 'Not set' : '…'}
            </span>
          </FieldCard>
          <FieldCard label="Front firmware">
            <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {raceCfgLoaded ? raceCfg?.fw_version_front || 'Not set' : '…'}
            </span>
          </FieldCard>
          <FieldCard label="Min battery (mV)">
            <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {raceCfgLoaded ? raceCfg?.min_battery ?? 'Not set' : '…'}
            </span>
          </FieldCard>
          <FieldCard label="Max debug log (B)">
            <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {raceCfgLoaded ? raceCfg?.max_debug_size ?? 'Not set' : '…'}
            </span>
          </FieldCard>
          <FieldCard label="Max race log (B)">
            <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {raceCfgLoaded ? raceCfg?.max_race_size ?? 'Not set' : '…'}
            </span>
          </FieldCard>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-8">
          <InlineSpinner />
        </div>
      ) : loadError ? (
        <div className="space-y-2">
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-200">
            {loadError}
          </div>
          <button type="button" onClick={() => void startEdit()} className={BTN_SECONDARY_SM}>
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <FieldCard label="Firmware">
              <select
                className={SELECT}
                value={selectedFirmwareValue}
                disabled={saving || firmwareLoading}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || v === '__orphan__') {
                    if (v === '') setForm((prev) => ({ ...prev, fw_version: '', fw_file_key: '' }));
                    return;
                  }
                  const art = firmwareArtifacts.find((a) => a.key === v);
                  if (art) setForm((prev) => ({ ...prev, fw_version: art.version, fw_file_key: art.key }));
                }}
              >
                <option value="">Select firmware</option>
                {firmwareArtifacts.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.version} — {a.fileName}
                  </option>
                ))}
                {form.fw_version && !firmwareArtifacts.some((a) => a.key === form.fw_file_key) ? (
                  <option value="__orphan__" disabled>
                    {form.fw_version} (current, not in list)
                  </option>
                ) : null}
              </select>
              {firmwareLoading ? (
                <div className="mt-1 text-[10px] text-gray-500 dark:text-slate-400">Loading firmware…</div>
              ) : null}
            </FieldCard>
            <FieldCard label="Front firmware">
              <select
                className={SELECT}
                value={selectedFrontFirmwareValue}
                disabled={saving || firmwareLoading}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || v === '__orphan__') {
                    if (v === '') setForm((prev) => ({ ...prev, fw_version_front: '', fw_file_key_front: '' }));
                    return;
                  }
                  const art = frontFirmwareArtifacts.find((a) => a.key === v);
                  if (art) setForm((prev) => ({ ...prev, fw_version_front: art.version, fw_file_key_front: art.key }));
                }}
              >
                <option value="">Select front firmware</option>
                {frontFirmwareArtifacts.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.version} — {a.fileName}
                  </option>
                ))}
                {form.fw_version_front && !frontFirmwareArtifacts.some((a) => a.key === form.fw_file_key_front) ? (
                  <option value="__orphan__" disabled>
                    {form.fw_version_front} (current, not in list)
                  </option>
                ) : null}
              </select>
              {firmwareLoading ? (
                <div className="mt-1 text-[10px] text-gray-500 dark:text-slate-400">Loading firmware…</div>
              ) : null}
            </FieldCard>
            <FieldCard label="Min battery (mV)">
              <input
                type="number"
                className={INPUT_SM}
                value={form.min_battery}
                disabled={saving}
                onChange={(e) => setForm((prev) => ({ ...prev, min_battery: toSafeNumber(e.target.value) }))}
              />
            </FieldCard>
            <FieldCard label="Max debug log (B)">
              <input
                type="number"
                className={INPUT_SM}
                value={form.max_debug_size}
                disabled={saving}
                onChange={(e) => setForm((prev) => ({ ...prev, max_debug_size: toSafeNumber(e.target.value) }))}
              />
            </FieldCard>
            <FieldCard label="Max race log (B)">
              <input
                type="number"
                className={INPUT_SM}
                value={form.max_race_size}
                disabled={saving}
                onChange={(e) => setForm((prev) => ({ ...prev, max_race_size: toSafeNumber(e.target.value) }))}
              />
            </FieldCard>
          </div>

          {meta.created_at > 0 || meta.edited_at > 0 ? (
            <p className="text-[11px] text-gray-500 dark:text-slate-400">
              {meta.edited_at > 0
                ? `Last edited by ${formatUserIdentityDisplay(meta.edited_by, globalUserIdToDisplayName)} · ${formatTs(meta.edited_at)}`
                : `Created by ${formatUserIdentityDisplay(meta.created_by, globalUserIdToDisplayName)} · ${formatTs(meta.created_at)}`}
            </p>
          ) : null}
        </div>
      )}
    </DetailCard>
  );
}
